import I18n from 'i18n-js'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { AuthContext } from '../../context/auth'
import { isCoordinator } from '../../router/permissions'
import medicalsCenter from '../../services/medicalsCenter'
import InputAutoComplete from '../inputAutoComplete'

// Reads the initial center from the URL query (?center=<id>). Returns a number or null.
export const useCenterFromQuery = () => {
  const { search } = useLocation()
  return useMemo(() => {
    const value = new URLSearchParams(search).get('center')
    const id = Number(value)
    return value && Number.isInteger(id) && id > 0 ? id : null
  }, [search])
}

// Clearable selector of the coordinator's medical centers. Renders nothing for other roles.
const CenterSelect = ({ value = null, onChange, className, size, sx, ...rest }) => {
  const { state } = useContext(AuthContext)
  const coordinator = isCoordinator(state)
  const [centers, setCenters] = useState([])

  useEffect(() => {
    if (!coordinator) return undefined
    let cancelled = false
    const load = async () => {
      const res = await medicalsCenter.list()
      if (!cancelled) setCenters(res?.data?.medicalCenters || [])
    }
    load()
    return () => {
      cancelled = true
    }
  }, [coordinator])

  if (!coordinator) return null

  const selected = centers.find((center) => center.id === value) || null

  return (
    <InputAutoComplete
      {...rest}
      className={className}
      size={size}
      sx={sx}
      label={
        selected
          ? I18n.t('coordinatingStaff.medicalCenter')
          : I18n.t('coordinatingStaff.allMyCenters')
      }
      options={centers}
      value={selected}
      onChange={(_, option) => onChange?.(option?.id ?? null)}
      getOptionLabel={(option) => option?.name ?? ''}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      noOptionsText={I18n.t('general.notFound')}
    />
  )
}

export default CenterSelect
